import React from 'react';
import { Card, Button, Container } from 'react-bootstrap';
import { InfoCircle, FileText, Building, Map, ArrowRight } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CollectionIcon } from '../../hooks/useCollections';
import './SearchResultCard.css';

import {
  truncateTitle,
  truncateDescription,
  stripHtmlTags,
  formatDate,
  openLinkInBrowser
} from './searchUtils';
import { feedsConfig } from '../../utils/feedsConfig';
import { departmentConfig } from '../../utils/departmentConfig';
import { canadaFeedsConfig } from '../../utils/canadaFeedsConfig';
import { canadaDepartmentConfig } from '../../utils/canadaDepartmentConfig';
import { enrichCanadaSearchResult } from '../../utils/canadaSearchSystem';
import { enrichMexicoSearchResult } from '../../utils/mexicoSearchSystem';
import { enrichUKSearchResult } from '../../utils/ukSearchSystem';

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Whole-word match. Plain includes() reported a Department of State hit on any
// bill mentioning "United States", because the department name stripped down to
// "state" is a substring of "States". A word boundary stops that, and also stops
// section keywords like 'dea' from matching "idea" or "deadline".
const matchesWord = (haystack, needle) => {
  if (!haystack || !needle) return false;
  return new RegExp(`\\b${escapeRegExp(needle)}\\b`, 'i').test(haystack);
};

// Stripped department names ('state', 'justice', 'labor') are ordinary English
// words. They're distinctive enough in structured feed metadata but not in a
// free-text document title, so the two tiers are scored separately below.
const MIN_SHORT_NAME_LENGTH = 4;

const SearchResultCard = ({ 
  item, 
  country, 
  onSave, 
  onContainerClick, 
  index,
  showCitations = false,
  isLegacyResult = false 
}) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const nameForAPI = item.collection_name?.toLowerCase().replace(/\s+/g, '_');
  const citationNumber = item.originalIndex || (index + 1);

  // Enhanced content type detection with multi-country support
  const enrichContentType = () => {
    try {
      const title = (item.title || '').toLowerCase();
      const description = (item.description || item.snippet || '').toLowerCase();
      const source = (item.source || '').toLowerCase();
      const collectionName = (item.collection_name || '').toLowerCase();

      // Use country-specific enrichment functions
      if (country === 'canada') {
        return enrichCanadaSearchResult(item);
      }

      if (country === 'mexico') {
        return enrichMexicoSearchResult(item);
      }

      if (country === 'uk' || country === 'united_kingdom') {
        return enrichUKSearchResult(item);
      }

      // Use US enrichment for US content (existing logic)
      if (!departmentConfig || Object.keys(departmentConfig).length === 0) {
        return { type: 'document' };
      }

      // Two passes over departments rather than first-match-wins. A full
      // department name anywhere in the item is a strong signal and returns
      // immediately; a bare short name is only trusted in source or
      // collection_name, and is held as a fallback so a full-name match on a
      // later department still beats it.
      let weakDepartmentMatch = null;

      for (const [deptKey, deptConfig] of Object.entries(departmentConfig)) {
        if (!deptConfig.departmentInfo) continue;

        const fullName = deptConfig.departmentInfo.name.toLowerCase();
        const shortName = fullName.replace(/^department of /i, '').trim();
        const keyName = deptKey.replace(/_/g, ' ').toLowerCase();

        const departmentResult = {
          type: 'department',
          departmentKey: deptKey,
          department: deptConfig.departmentInfo,
          route: deptConfig.departmentInfo.route
        };

        if (matchesWord(title, fullName) ||
            matchesWord(source, fullName) ||
            matchesWord(collectionName, fullName)) {
          return departmentResult;
        }

        if (!weakDepartmentMatch) {
          const weakKeywords = [shortName, keyName]
            .filter(keyword => keyword && keyword.length >= MIN_SHORT_NAME_LENGTH);

          const hasWeakMatch = weakKeywords.some(keyword =>
            matchesWord(source, keyword) || matchesWord(collectionName, keyword)
          );

          if (hasWeakMatch) {
            weakDepartmentMatch = departmentResult;
          }
        }
      }

      if (weakDepartmentMatch) {
        return weakDepartmentMatch;
      }

      // Simple section detection for US
      const sectionKeywords = {
        'defense': ['defense', 'military', 'pentagon'],
        'health': ['health', 'medical', 'healthcare'],
        'justice': ['justice', 'fbi', 'dea'],
        'treasury': ['treasury', 'economic', 'finance']
      };

      for (const [sectionKey, keywords] of Object.entries(sectionKeywords)) {
        if (keywords.some(keyword => matchesWord(title, keyword) || matchesWord(description, keyword))) {
          return {
            type: 'section',
            sectionKey: sectionKey,
            route: `/${country}/${sectionKey}`
          };
        }
      }

      return { type: 'document' };
    } catch (error) {
      console.warn('Error in enrichContentType:', error);
      return { type: 'document' };
    }
  };

  const contentType = enrichContentType();

  // The saved payload is built up front so the folder icon can check the same
  // object against the collection. Saving still goes through the onSave prop —
  // this component doesn't own the collection, it only reports the item.
  const buildSavePayload = () => {
    if (isLegacyResult) {
      // Legacy API format
      return {
        title: item.title,
        detailsLink: item.url,
        docLink: item.document_url || '',
        date: formatDate(item.created_at),
        feedName: item.collection_name || '',
        branchName: item.branch || '',
        topicName: item.topic || '',
        description: item.description
          ? stripHtmlTags(truncateDescription(item.description, 297))
          : ''
      };
    }

    // New API format
    return {
      title: item.title,
      detailsLink: item.url,
      docLink: '',
      date: formatDate(item.date || item.last_updated),
      feedName: '',
      branchName: '',
      topicName: '',
      description: item.snippet ? stripHtmlTags(truncateDescription(item.snippet, 297)) : ''
    };
  };

  const handleSave = () => {
    onSave(buildSavePayload());
  };

  const handleInternalNavigation = () => {
    if (contentType.route) {
      navigate(contentType.route);
    }
  };

  const getContentTypeLabel = () => {
    switch (contentType.type) {
      case 'department':
        return contentType.department.name;
      case 'agency':
        return `${contentType.agency?.shortName || contentType.agency?.name}`;
      case 'province':
        return t('common:nameGovernment', '{{name}} Government', { name: contentType.province?.fullName });
      case 'region': // Added for UK regions
        return t('common:nameGovernment', '{{name}} Government', { name: contentType.region?.fullName });
      case 'state': {
        const stateName = contentType.stateName
          ? contentType.stateName.charAt(0).toUpperCase() + contentType.stateName.slice(1)
          : '';
        return t('common:nameGovernment', '{{name}} Government', { name: stateName });
      }
      case 'section': {
        const sectionName = contentType.sectionKey
          ? contentType.sectionKey.charAt(0).toUpperCase() + contentType.sectionKey.slice(1)
          : '';
        return t('common:sectionLabel', '{{name}} Section', { name: sectionName });
      }
      default:
        return null;
    }
  };

  const getContentTypeIcon = () => {
    switch (contentType.type) {
      case 'department':
      case 'agency':
        return <Building size={14} className="me-1" />;
      case 'province':
      case 'region': // Added for UK regions
      case 'state':
        return <Map size={14} className="me-1" />;
      case 'section':
        return <InfoCircle size={14} className="me-1" />;
      default:
        return null;
    }
  };

  return (
    <div className="d-flex align-items-center justify-content-center" id="searchPadding">
      <Container id="searchFeedContainer">
        {/* Citation number badge for new API results */}
        {showCitations && !isLegacyResult && (
          <div 
            id={`citation-${citationNumber}`}
            className="search-citation-badge"
          >
            {citationNumber}
          </div>
        )}

        {/* Enhanced content type indicator */}
        {/* {contentType.type !== 'document' && (
          <Card.Text 
            className={`search-content-type-indicator ${contentType.route ? 'clickable' : ''}`}
            onClick={contentType.route ? handleInternalNavigation : undefined}
          >
            {getContentTypeIcon()}
            {getContentTypeLabel()}
            {contentType.route && (
              <ArrowRight size={12} className="ms-1" />
            )}
          </Card.Text>
        )} */}

        {/* Legacy API: Collection name as clickable title */}
        {isLegacyResult && (
          <Card.Title
            id="billBodyTextSource"
            onClick={() => {
              if (contentType.route) {
                handleInternalNavigation();
              } else {
                onContainerClick(item.collection_name, nameForAPI);
              }
            }}
          >
            {item.collection_name}
          </Card.Title>
        )}

        {/* Main title */}
        <Card.Body id="billBodyText" onClick={() => openLinkInBrowser(item.url)}>
          {isLegacyResult 
            ? truncateTitle(item.status_title || item.title)
            : truncateTitle(item.title)
          }
        </Card.Body>

        {/* Date */}
        <Card.Body id="billBodyText">
          {isLegacyResult 
            ? formatDate(item.created_at)
            : formatDate(item.date || item.last_updated)
          }
        </Card.Body>

        {/* Last updated for new API */}
        {!isLegacyResult && item.last_updated && (
          <Card.Body id="billBodyText">
            {t('common:lastUpdated', 'Last Updated: {{date}}', { date: formatDate(item.last_updated) })}
          </Card.Body>
        )}

        {/* Description/Snippet */}
        {(item.description || item.snippet) && (
          <Card.Body id="billBodyText" onClick={() => openLinkInBrowser(item.url)}>
            {isLegacyResult 
              ? stripHtmlTags(truncateDescription(item.description, 297))
              : truncateDescription(item.snippet, 297)
            }
          </Card.Body>
        )}

        {/* Source (legacy API only) with enhanced display */}
        {isLegacyResult && item.source && (
          <Card.Body id="billBodyText" className="search-source-text">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <span>{item.source?.replace(/_/g, ' ')
                .replace(/\b\w/g, char => char.toUpperCase())
                .replace(/ Archived/g, '').trim()}</span>
              {item.branch && item.branch !== 'N/A' && (
                <span className="badge search-badge-secondary">{item.branch}</span>
              )}
              {item.topic && item.topic !== 'N/A' && (
                <span className="badge search-badge-info">{item.topic}</span>
              )}
            </div>
          </Card.Body>
        )}

        {/* Action buttons */}
        <div className="d-flex gap-2 flex-wrap">
          <Button id="billLink" onClick={() => openLinkInBrowser(item.url)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg> {t('common:source', 'Source')}
          </Button>

          {/* Document button (legacy API only) */}
          {isLegacyResult && item.document_url && (
            <Button id="viewBillButton" onClick={() => openLinkInBrowser(item.document_url)}>
              <FileText /> {t('common:document', 'Document')}
            </Button>
          )}

          {/* Internal navigation button */}
          {contentType.route && (
            <Button
              id="billLink"
              onClick={handleInternalNavigation}
            >
              {contentType.type === 'department' && <Building size={14} />}
              {contentType.type === 'agency' && <Building size={14} />}
              {(contentType.type === 'state' || contentType.type === 'province' || contentType.type === 'region') && <Map size={14} />}
              {contentType.type === 'section' && <InfoCircle size={14} />}
              <span className="ms-1">{t('common:explore', 'Explore')}</span>
            </Button>
          )}

          <Button id="addToCollectionButton2" onClick={handleSave}>
            <CollectionIcon item={buildSavePayload()} />
          </Button>
        </div>
      </Container>
    </div>
  );
};

export default SearchResultCard;